// src/context/AuthProvider.tsx
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import api from "../lib/api";
import { clearToken, getToken, LS_TOKEN, setToken, type PersistMode } from "../lib/auth";

export type User = {
  id: number;
  name: string;
  email: string;
  is_admin: boolean;
  ekyc_status?: "verified" | "pending" | "unverified" | null;
  account_status?: "verified" | "pending" | "unverified" | null;
};

type SignupInput =
  | { name: string; email: string; password: string }
  | { name: string; email: string; password: string; idcard: File };

type Ctx = {
  user: User | null;
  ready: boolean;
  login: (email: string, password: string, remember?: boolean) => Promise<User>;
  logout: () => Promise<void>;
  signup: (p: SignupInput) => Promise<void>;
  refresh: () => Promise<User | null>;
};

const AuthCtx = createContext<Ctx | null>(null);

export const useAuth = () => {
  const v = useContext(AuthCtx);
  if (!v) throw new Error("useAuth must be used inside AuthProvider");
  return v;
};

// axios: 쿠키 동반
api.defaults.withCredentials = true;

export default function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [ready, setReady] = useState(false);

  // Authorization 헤더 관리
  const setAuthHeader = (token: string | null, mode: PersistMode = "local") => {
    if (token) {
      api.defaults.headers.common.Authorization = `Bearer ${token}`;
      setToken(token, mode);
    } else {
      delete api.defaults.headers.common.Authorization;
      clearToken();
    }
  };

  const refresh = async (): Promise<User | null> => {
    try {
      const { data } = await api.get("/me");
      const u: User | null = data?.user ?? data ?? null;
      setUser(u);
      return u;
    } catch {
      setUser(null);
      return null;
    } finally {
      setReady(true);
    }
  };

  useEffect(() => {
    // 초기화: 저장된 토큰을 헤더에 연결하고 me 조회
    const token = getToken();
    if (token) {
      api.defaults.headers.common.Authorization = `Bearer ${token}`;
    } else {
      delete api.defaults.headers.common.Authorization;
    }
    refresh();

    // 탭 간 토큰 동기화: localStorage 토큰 기준
    const onStorage = (e: StorageEvent) => {
      if (e.key === LS_TOKEN) {
        const t = localStorage.getItem(LS_TOKEN);
        if (t) {
          api.defaults.headers.common.Authorization = `Bearer ${t}`;
        } else {
          delete api.defaults.headers.common.Authorization;
        }
        refresh();
      }
    };

    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (
    email: string,
    password: string,
    remember: boolean = true
  ): Promise<User> => {
    const { data } = await api.post("/login", { email, password, remember });

    const token: string | undefined = data?.token;
    const mode: PersistMode = remember ? "local" : "session";
    setAuthHeader(token ?? null, mode);

    const me = await refresh();
    if (!me) throw new Error("로그인 후 사용자 정보 로드 실패");
    return me;
  };

  const signup = async (p: SignupInput) => {
    if ("idcard" in p && p.idcard instanceof File) {
      const fd = new FormData();
      fd.append("name", p.name);
      fd.append("email", p.email);
      fd.append("password", p.password);
      fd.append("idcard", p.idcard);
      await api.post("/signup", fd);
    } else {
      await api.post("/signup", p as { name: string; email: string; password: string });
    }
  };

  const logout = async () => {
    try {
      await api.post("/logout").catch(() => {});
    } finally {
      setAuthHeader(null);
      setUser(null);
      setReady(true);
    }
  };

  const value = useMemo(
    () => ({ user, ready, login, logout, signup, refresh }),
    [user, ready]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}
